import Image from "next/image";
import Link from "next/link";

interface BlogPostCardProps {
  title: string;
  description: string;
  slug: string;
  category: "ppf" | "keraamika" | "toonimine" | "hooldus";
  date: string;
  readingTime: string;
  coverImage?: string;
}

const categoryLabels: Record<string, string> = {
  ppf: "PPF",
  keraamika: "Keraamika",
  toonimine: "Toonimine",
  hooldus: "Hooldus",
};

function formatDate(value: string) {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("et-EE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function BlogPostCard({
  title,
  description,
  slug,
  category,
  date,
  readingTime,
  coverImage,
}: BlogPostCardProps) {
  const categoryFallback: Record<string, string> = {
    ppf: "/services/Kaitsekiletamine%20PPF.png",
    keraamika: "/services/Keraamiline%20kaitse.png",
    toonimine: "/services/Klaaside%20toonimine.png",
    hooldus: "/services/Keraamiline%20kaitse.png",
  };

  const imageFallback = categoryFallback[category] || "/images/blog/default-cover.jpg";

  const imageSrc = coverImage || imageFallback;

  return (
    <Link href={`/blog/${slug}`} className="group block h-full">
      <article className="flex h-full flex-col overflow-hidden rounded-sm border border-white/10 bg-secondary transition-colors duration-300 hover:border-accent/40 hover:shadow-[0_16px_45px_rgba(0,0,0,0.35)]">
        <div className="relative h-48 w-full overflow-hidden bg-primary">
          <Image
            src={imageSrc}
            alt={title}
            fill
            className="object-cover grayscale-[60%] brightness-90 transition-[filter,transform] duration-300 ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
        <div className="flex flex-1 flex-col p-7">
          <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-accent">
            <span>{categoryLabels[category] || category}</span>
            <span>{readingTime}</span>
          </div>
          <h2
            className="blog-card-title mt-5 line-clamp-3 font-heading text-2xl font-semibold text-text-main"
            title={title}
          >
            {title}
          </h2>
          <p className="mt-4 flex-1 line-clamp-3 text-sm leading-relaxed text-text-muted">{description}</p>
          <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-sm text-text-muted">
            <span>{formatDate(date)}</span>
            <span className="font-medium text-accent transition group-hover:translate-x-1">Loe edasi →</span>
          </div>
        </div>
      </article>
    </Link>
  );
}
